import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, useToast } from '../../components/ui';
import { channelsApi } from '../../api/client';
import { useAppContext } from '../../store/AppContext';
import type { Message, Agent } from '../../api/types';

interface Props {
  channelId: string;
  onOpenThread: (message: Message) => void;
}

const MAX_MESSAGES = 100;

export const MessageFeed: React.FC<Props> = ({ channelId, onOpenThread }) => {
  const { messages, setMessages, channels, agents } = useAppContext();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [routingPolicy, setRoutingPolicy] = useState<'broadcast' | 'direct'>('broadcast');
  const [targetAgentId, setTargetAgentId] = useState('');
  const [pollCursor, setPollCursor] = useState<string | null>(null);
  const [pollInterval, setPollInterval] = useState(3000);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const channel = channels.find((c) => c.id === channelId);
  const isObservable = channel?.type === 'observable';

  // Initial load
  useEffect(() => {
    if (!channelId) return;
    setLoading(true);
    channelsApi.messages(channelId).then((res) => {
      if (res.ok) {
        setMessages(res.data.slice(-MAX_MESSAGES));
      }
      setLoading(false);
    });
  }, [channelId, setMessages]);

  // Poll for new messages using /polling API (cursor-based, dynamic interval)
  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;
    let currentCursor = pollCursor;
    let currentInterval = pollInterval;

    const poll = async () => {
      if (cancelled) return;
      const res = await channelsApi.poll([channelId], currentCursor ?? undefined);
      if (!cancelled && res.ok) {
        const { events, last_cursor, poll_interval_ms } = res.data;
        if (events && events.length > 0) {
          // events may be wrapped {kind,data} or flat message objects
          const newMsgs: Message[] = events.map((e: unknown) => {
            const ev = e as Record<string, unknown>;
            return (ev.kind === 'message' && ev.data ? ev.data : ev) as Message;
          });
          setMessages([...messages, ...newMsgs.filter(m => !messages.find(p => p.id === m.id))].slice(-MAX_MESSAGES));
          currentCursor = last_cursor;
          setPollCursor(last_cursor);
        }
        if (poll_interval_ms && poll_interval_ms !== currentInterval) {
          currentInterval = poll_interval_ms;
          setPollInterval(poll_interval_ms);
        }
      }
      if (!cancelled) {
        setTimeout(poll, currentInterval);
      }
    };

    const timerId = setTimeout(poll, currentInterval);
    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async () => {
    if (!draft.trim() || sending) return;
    const content = draft.trim();
    setDraft('');
    setSending(true);
    try {
      const sendRes = await channelsApi.sendMessage(channelId, content, {
        routingPolicy,
        targetAgentId: routingPolicy === 'direct' && targetAgentId ? targetAgentId : undefined,
      });
      if (!sendRes.ok) {
        showToast(sendRes.error?.message ?? 'Failed to send message', 'error');
        setDraft(content);
      } else {
        const res = await channelsApi.messages(channelId);
        if (res.ok) setMessages(res.data.slice(-MAX_MESSAGES));
      }
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const channelMessages = messages.filter((m) => m.channelId === channelId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Channel header */}
      <div style={{
        padding: '12px 20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'white',
        flexShrink: 0,
      }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 16 }}>#</span>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{channel?.name ?? channelId}</span>
        {isObservable && (
          <Badge variant="info">Observable</Badge>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button style={{
            width: 30, height: 30, border: '1px solid var(--border)',
            borderRadius: 6, background: 'white', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, color: 'var(--text-secondary)',
          }}>🔍</button>
          <button style={{
            width: 30, height: 30, border: '1px solid var(--border)',
            borderRadius: 6, background: 'white', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, color: 'var(--text-secondary)',
          }}>👥</button>
        </div>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflow: 'auto', padding: '16px 20px',
        display: 'flex', flexDirection: 'column', gap: 4,
        background: 'white',
      }}>
        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'var(--border)', flexShrink: 0,
                  animation: 'pulse 1.5s ease-in-out infinite',
                }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ height: 12, width: `${60 + (i % 3) * 20}px`, borderRadius: 4, background: 'var(--border)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ height: 32, width: `${160 + (i % 4) * 40}px`, borderRadius: 8, background: 'var(--bg-secondary)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && channelMessages.length === 0 && (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 48, fontSize: 13 }}>
            No messages yet. Say hello! 👋
          </div>
        )}
        {channelMessages.map((msg) => (
          <MessageRow
            key={msg.id}
            message={msg}
            agents={agents}
            onOpenThread={() => onOpenThread(msg)}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div style={{
        padding: '12px 20px',
        borderTop: '1px solid var(--border)',
        background: 'white',
        flexShrink: 0,
      }}>
        {isObservable ? (
          <div style={{
            border: '1px solid var(--border)', borderRadius: 10,
            padding: '12px 16px', textAlign: 'center',
            background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
            fontSize: 13,
          }}>
            <span>👁️ This is an Observable channel.</span>{' '}
            <button style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--primary)', fontWeight: 600, fontSize: 13,
            }}>
              ✋ Request to join discussion
            </button>
          </div>
        ) : (
          <div style={{
            border: '1px solid var(--border)',
            borderRadius: 10, overflow: 'hidden',
            transition: 'border-color 0.15s',
          }}
          onFocusCapture={(e) => (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary)'}
          onBlurCapture={(e) => (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'}
          >
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Message #${channel?.name ?? channelId}`}
              rows={2}
              style={{
                width: '100%', padding: '10px 14px',
                border: 'none', resize: 'none',
                fontSize: 13, outline: 'none',
                fontFamily: 'inherit', color: 'var(--text)',
                background: 'white', minHeight: 40,
              }}
            />
            <div style={{
              padding: '6px 10px', display: 'flex',
              alignItems: 'center', gap: 6,
              background: 'var(--bg-secondary)',
              borderTop: '1px solid var(--border)',
            }}>
              {/* Route toggle */}
              <button
                onClick={() => setRoutingPolicy(routingPolicy === 'broadcast' ? 'direct' : 'broadcast')}
                style={{
                  padding: '2px 8px', borderRadius: 10,
                  border: 'none', cursor: 'pointer', fontSize: 11,
                  fontWeight: 600,
                  background: routingPolicy === 'direct' ? 'var(--primary-light)' : 'transparent',
                  color: routingPolicy === 'direct' ? 'var(--primary)' : 'var(--text-secondary)',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                {routingPolicy === 'broadcast' ? '📢 Broadcast' : '→ Direct'}
              </button>
              {routingPolicy === 'direct' && agents.length > 0 && (
                <select
                  value={targetAgentId}
                  onChange={(e) => setTargetAgentId(e.target.value)}
                  style={{
                    padding: '2px 6px', borderRadius: 5,
                    border: '1px solid var(--border)', fontSize: 11,
                    color: 'var(--text)', background: 'white',
                  }}
                >
                  <option value="">Select agent…</option>
                  {agents.map((a: Agent) => (
                    <option key={a.id} value={a.id}>{a.displayName}</option>
                  ))}
                </select>
              )}
              <div style={{ flex: 1 }} />
              <button
                onClick={handleSend}
                disabled={!draft.trim() || sending}
                style={{
                  padding: '5px 14px', background: 'var(--primary)',
                  color: 'white', border: 'none', borderRadius: 6,
                  cursor: draft.trim() && !sending ? 'pointer' : 'not-allowed',
                  fontSize: 12, fontWeight: 600,
                  opacity: !draft.trim() || sending ? 0.5 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                {sending ? '…' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Avatar colors for agents vs users
const AGENT_COLORS = ['#4F46E5', '#7C3AED', '#0891B2', '#059669', '#DC2626', '#D97706'];
function colorForName(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AGENT_COLORS[Math.abs(h) % AGENT_COLORS.length];
}

function MessageRow({
  message,
  agents,
  onOpenThread,
}: {
  message: Message;
  agents: Agent[];
  onOpenThread: () => void;
}) {
  const senderName = message.agent?.displayName ?? message.user?.displayName ?? 'Unknown';
  const isAgent = !!message.agent;
  const initials = senderName.slice(0, 2).toUpperCase();
  const avatarColor = colorForName(senderName);
  const time = new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const targetAgent = message.targetAgentId ? agents.find((a) => a.id === message.targetAgentId) : null;
  const isDirect = message.routingPolicy === 'direct';

  // System message
  if (message.type === 'system') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {message.content}
        </span>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>
    );
  }

  // Report message
  if (message.type === 'report') {
    return (
      <div style={{ margin: '8px 0', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{
          background: 'var(--bg-secondary)', padding: '8px 14px',
          fontSize: 12, color: 'var(--text-secondary)',
          display: 'flex', alignItems: 'center', gap: 6,
          borderBottom: '1px solid var(--border)',
        }}>
          <span>📊</span>
          <span style={{ fontWeight: 500 }}>Report — {senderName}</span>
          <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>{time}</span>
        </div>
        <div style={{
          padding: 14, background: '#FFFBF0',
          fontSize: 13, fontFamily: 'monospace',
          whiteSpace: 'pre-wrap', color: 'var(--text)',
        }}>
          {message.content}
        </div>
      </div>
    );
  }

  // Interactive message
  if (message.type === 'interactive') {
    return (
      <div style={{
        margin: '8px 0',
        border: '1px solid #E0E7FF',
        borderRadius: 10, overflow: 'hidden',
        background: '#F5F3FF',
      }}>
        <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: '#7C3AED', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: 'white', flexShrink: 0,
          }}>{initials}</div>
          <span style={{ fontWeight: 600, fontSize: 13 }}>{senderName}</span>
          <Badge variant="purple">Interactive</Badge>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{time}</span>
        </div>
        <div style={{ padding: '0 14px 12px', fontSize: 13, lineHeight: 1.5 }}>{message.content}</div>
        <div style={{ padding: '8px 14px', borderTop: '1px solid #E0E7FF', display: 'flex', gap: 8 }}>
          <button style={{
            padding: '4px 12px', background: 'var(--primary)',
            color: 'white', border: 'none', borderRadius: 6,
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>Approve</button>
          <button style={{
            padding: '4px 12px', background: 'white',
            color: 'var(--text-secondary)', border: '1px solid var(--border)',
            borderRadius: 6, fontSize: 12, cursor: 'pointer',
          }}>Reject</button>
        </div>
      </div>
    );
  }

  // Standard message (text + direct)
  // Human: right-aligned blue bubble; Agent: left-aligned yellow/gray bubble
  const isHuman = !!message.senderUserId && !message.agent;
  const bubbleBg = isHuman
    ? 'var(--primary)'          // blue for human
    : isDirect ? 'transparent'
    : '#FFFBEB';                // warm yellow for agent
  const bubbleColor = isHuman ? 'white' : 'var(--text)';
  const bubbleRadius = isHuman ? '8px 0 8px 8px' : '0 8px 8px 8px';

  return (
    <div style={{
      display: 'flex', gap: 12, marginTop: 10,
      flexDirection: isHuman ? 'row-reverse' : 'row',
      ...(isDirect && !isHuman ? {
        borderLeft: '3px solid var(--primary)',
        paddingLeft: 12, marginLeft: -12,
        background: 'var(--primary-light)',
        borderRadius: '0 8px 8px 0',
      } : {}),
    }}>
      {/* Avatar — hidden for human (right side) */}
      {!isHuman && (
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: avatarColor, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 13, fontWeight: 700,
        }}>
          {initials}
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: isHuman ? 'flex-end' : 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3, flexDirection: isHuman ? 'row-reverse' : 'row' }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>{senderName}</span>
          {isAgent && <Badge variant="purple">Agent</Badge>}
          {isDirect && targetAgent && (
            <span style={{
              fontSize: 11, color: 'var(--primary)', fontWeight: 600,
              background: '#E0E7FF', padding: '1px 6px', borderRadius: 8,
              display: 'flex', alignItems: 'center', gap: 3,
            }}>
              → @{targetAgent.displayName}
            </span>
          )}
          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{time}</span>
        </div>
        <div style={{
          background: bubbleBg,
          color: bubbleColor,
          padding: '8px 12px', borderRadius: bubbleRadius,
          fontSize: 13, lineHeight: 1.5,
          display: 'inline-block', maxWidth: 500,
        }}>
          {message.content}
        </div>
        {(message._count?.threadReplies ?? 0) > 0 && (
          <button
            onClick={onOpenThread}
            style={{
              marginTop: 4, background: 'none', border: 'none',
              cursor: 'pointer', color: 'var(--primary)',
              fontSize: 11, fontWeight: 500, padding: '2px 0',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            💬 {message._count!.threadReplies}{' '}
            {message._count!.threadReplies === 1 ? 'reply' : 'replies'}
          </button>
        )}
      </div>
    </div>
  );
}
